import { Nullish } from '@omnea-digital/toolbox'
import { createContext, ReactNode, useContext, useMemo } from 'react'
import { Dependency, isSupplierDependency, isSupplierProfileDependency } from '@omnea-app/core-data/coreDataFrontendClient'

// A supplier may read core data only through the supplier-portal endpoints, which serve what one request form
// uses and leave out the buyer's ERP ids.
export type CoreDataScope = { kind: 'internal' } | { kind: 'supplierForm'; requestFormId: string }

export const INTERNAL_CORE_DATA_SCOPE: CoreDataScope = { kind: 'internal' }

const CoreDataScopeContext = createContext<CoreDataScope>(INTERNAL_CORE_DATA_SCOPE)

type SupplierFormCoreDataScopeProviderProps = {
  requestFormId: string
  children: ReactNode
}

export const SupplierFormCoreDataScopeProvider = ({ requestFormId, children }: SupplierFormCoreDataScopeProviderProps) => {
  const scope = useMemo<CoreDataScope>(() => ({ kind: 'supplierForm', requestFormId }), [requestFormId])
  return <CoreDataScopeContext.Provider value={scope}>{children}</CoreDataScopeContext.Provider>
}

export const useCoreDataScope = (): CoreDataScope => useContext(CoreDataScopeContext)

// Spread at the end of a query key: internal keys stay exactly as they were, so their invalidation prefixes still match.
export const coreDataScopeQueryKey = (scope: CoreDataScope): [] | [{ requestFormId: string }] =>
  scope.kind === 'internal' ? [] : [{ requestFormId: scope.requestFormId }]

// The supplier-portal endpoints return an option's links to a supplier only for the supplier the request names.
export const getDependencySupplierId = (dependency: Nullish<Dependency>): string | undefined => {
  if (isSupplierDependency(dependency)) return dependency.organisationSupplierId ?? undefined
  if (isSupplierProfileDependency(dependency)) return dependency.supplierId ?? undefined
  return undefined
}

// Supplier items carry every informational field but none of the buyer's ERP ids; filling them empty keeps the
// internal item shape, so a supplier sees no ERP pills and cannot search by remote id.
export const withNoErpReferences = <Item extends object>(item: Item) => ({
  ...item,
  externalReferences: [],
  remoteId: null,
  archivedBy: null,
  managingSource: null,
})
